import { getBlogListCollection, GetBlogListParameters } from "@bevco/Services/BlogListService";
import { initialBlogState } from "@bevco/Store/State/Blog/BlogListState";
import { BlogSchemas } from "@bevco/Types/CustomModels";
import { injectMetadata, removeMetadata } from "@bevco/Utilities/Metadata";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { createHandlerChainRunner, Handler, HandlerProps } from "@insite/client-framework/HandlerCreator";
import { HasPagingParameters } from "@insite/client-framework/Services/ApiService";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import cloneDeep from "lodash/cloneDeep";
import merge from "lodash/merge";

interface Parameter {
    pageSize?: number;
    page?: number;
    pageId?: string;
    search?: string;
    isLandingPage?: boolean;
    parameters: HasPagingParameters;
    isFirstLoad: boolean;
}

interface Props {
    getBlogListParameters?: GetBlogListParameters;
}

type HandlerType = Handler<Parameter, Props>;

const dispatchIsLoading = (isLoading: boolean, props: HandlerProps<Parameter, Props>) => {
    props.dispatch({
        type: "Custom/BlogListState/Loading",
        isLoading,
    } as any);
};

export const SetLoadingTrue: HandlerType = props => {
    dispatchIsLoading(true, props);
};

export const ClearStateOnFirstLoad: HandlerType = props => {
    if (props.parameter.isFirstLoad) {
        props.dispatch({
            type: "Custom/BlogListState/Filter",
            parameters: initialBlogState.parameters,
        } as any);

        props.dispatch({
            type: "Custom/BlogListState/Posts",
            posts: initialBlogState.posts,
        } as any);

        props.dispatch({
            type: "Pages/ProductList/SetFilterQuery",
            result: "",
        });
    }
};

export const MergePaginationParameters: HandlerType = props => {
    const stateFilters: HasPagingParameters & { pageId?: string } = cloneDeep(props.parameter.parameters);
    let newFilters = stateFilters;

    if (!props.parameter.isLandingPage) {
        const { pageSize, page } = parseQueryString<{ pageSize?: string; page?: string }>(props.parameter.search || "");
        if (pageSize) {
            newFilters = merge(newFilters, { pageSize: convertToNumberIfString(pageSize) });
        }

        if (page) {
            newFilters = merge(newFilters, { page: convertToNumberIfString(page) });
        }

        newFilters.pageId = props.parameter.pageId;
    } else {
        newFilters = merge(newFilters, { page: 1 });
    }

    props.getBlogListParameters = { ...newFilters };

    props.dispatch({
        type: "Custom/BlogListState/Filter",
        parameters: newFilters,
    } as any);
};

export const FetchBlogList: HandlerType = props => {
    const blogParameters = props.getBlogListParameters;

    if (!blogParameters) {
        dispatchIsLoading(false, props);
        return;
    }

    getBlogListCollection(blogParameters)
        .then(data => {
            const { pagination, posts } = data || {};

            if (posts) {
                props.dispatch({
                    type: "Custom/BlogListState/Posts",
                    posts,
                } as any);
            }

            if (pagination) {
                props.dispatch({
                    type: "Custom/BlogListState/Pagination",
                    pagination,
                } as any);
            }

            removeMetadata(BlogSchemas.blogListingWidget);
            const hostOrigin = typeof window === "undefined" ? "" : window.location.origin;
            const schemaData = {
                "@context": "https://schema.org/",
                "@type": "ItemList",
                itemListElement: (posts ?? []).map((post, idx) => ({
                    "@type": "ListItem",
                    position: idx + 1,
                    name: post.name,
                    url: `${hostOrigin}${post.url}`,
                })),
            };
            injectMetadata(JSON.stringify(schemaData), BlogSchemas.blogListingWidget);
        })
        .finally(() => dispatchIsLoading(false, props));
};

export const chain = [ClearStateOnFirstLoad, SetLoadingTrue, MergePaginationParameters, FetchBlogList];

const loadBlogList = createHandlerChainRunner(chain, "LoadBlogList");
export default loadBlogList;
